import { useState, useMemo, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FuelCharge } from '../types/fuel-charge-types';
import { useJobTaskApi } from '../../../../../clients/quipli-service/job-tasks/job-task-api';
import { Order } from '../../../../../clients/quipli-service/order/order';
import { JobType, MachineHoursMetadata, TaskType } from '../../../../../clients/quipli-service/job-tasks/job-tasks';

export const useFuelChargeManagement = (order?: Order, showInventoryReturn?: boolean) => {
  const { getJobByEntity } = useJobTaskApi();
  const [fuelCharges, setFuelCharges] = useState<FuelCharge[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const hasAnyFuelCharge = useMemo(() => fuelCharges.some((fc) => fc.amount > 0), [fuelCharges]);

  const init = async () => {
    setLoading(true);
    try {
      if (!order) {
        return;
      }

      if (!showInventoryReturn) {
        setFuelCharges([]);
        return;
      }

      const job = await getJobByEntity('Order', `${order.id}`, JobType.CheckinInspection);
      if (job) {
        const tasks = job.tasks;
        const fuelTasks = tasks.filter((t) => t.type === TaskType.MachineHours && t.status === 'Complete');

        fuelTasks.forEach((t) => {
          const metadata = t.metadata as MachineHoursMetadata;

          // Validate that metadata exists and has required fields, since it's kind of arbitrary and dont want it to break (:
          if (
            !metadata ||
            typeof metadata.fuel !== 'number' ||
            typeof metadata.orderId !== 'number' ||
            typeof metadata.productUnitId !== 'number'
          ) {
            console.warn('Skipping task with invalid metadata:', t.id, metadata);
            return;
          }

          const fuelCharges = metadata.fuel;
          setFuelCharges((prev) => {
            const existingForProductId = prev.find((fc) => fc.unitId === metadata.productUnitId);
            if (existingForProductId) {
              return prev.map((fc) => (fc.uuid === existingForProductId.uuid ? { ...fc, amount: fuelCharges } : fc));
            } else {
              const orderProduct = order.products.find((op) =>
                op.assignedUnits.some((au) => au.id === metadata.productUnitId),
              );
              const orderProductId = orderProduct?.id ?? 0;
              const fuelProductId = orderProduct?.product.fuelChargeProductId ?? 0;

              return [
                ...prev,
                {
                  orderProductId,
                  fuelChargeProductId: fuelProductId,
                  unitId: metadata.productUnitId,
                  amount: fuelCharges,
                  uuid: uuidv4() + 'fuel',
                },
              ];
            }
          });
        });
      }
    } catch (error) {
      console.error('Error loading fuel amount from check-in inspection', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (order) {
      init();
    }
  }, [order, showInventoryReturn]);

  const addFuelCharge = useCallback((orderProductId: number, unitId: number, amount: number, productId?: number) => {
    setFuelCharges((prev) => {
      const existing = prev.find((fc) => fc.orderProductId === orderProductId && fc.unitId === unitId);

      if (existing) {
        return prev.map((fc) =>
          fc.uuid === existing.uuid ? { ...fc, amount, fuelChargeProductId: productId || fc.fuelChargeProductId } : fc,
        );
      }

      return [
        ...prev,
        {
          orderProductId,
          fuelChargeProductId: productId,
          unitId,
          amount,
          uuid: uuidv4() + 'fuel',
        },
      ];
    });
  }, []);

  const removeFuelChargesForProduct = useCallback((orderProductId: number) => {
    setFuelCharges((prev) => prev.filter((fc) => fc.orderProductId !== orderProductId));
  }, []);

  const getFuelChargeForUnit = useCallback(
    (orderProductId: number, unitId: number) => {
      return fuelCharges.find((fc) => fc.orderProductId === orderProductId && fc.unitId === unitId);
    },
    [fuelCharges],
  );

  const updateFuelCharge = useCallback(
    (orderProductId: number, unitId: number, updates: { amount: number; productId?: number }) => {
      setFuelCharges((prev) => {
        const existing = prev.find((fc) => fc.orderProductId === orderProductId && fc.unitId === unitId);

        if (existing) {
          // Update existing fuel charge
          return prev.map((fc) =>
            fc.uuid === existing.uuid
              ? { ...fc, amount: updates.amount, fuelChargeProductId: updates.productId || fc.fuelChargeProductId }
              : fc,
          );
        } else {
          // Create new fuel charge if it doesn't exist
          return [
            ...prev,
            {
              orderProductId,
              unitId,
              amount: updates.amount,
              fuelChargeProductId: updates.productId,
              uuid: uuidv4() + 'fuel',
            },
          ];
        }
      });
    },
    [],
  );

  return {
    // State
    fuelCharges,
    hasAnyFuelCharge,
    loading,

    // Actions
    setFuelCharges,
    addFuelCharge,
    removeFuelChargesForProduct,

    // Helper functions
    getFuelChargeForUnit,
    updateFuelCharge,
  };
};
